import { FC, useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { FormsV1 } from '@frontend/api-digital-forms';
import { http } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { useCountdown } from '@frontend/timer';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, FormRow, Heading, Text, TextLink, useAlert } from '@frontend/design-system';
import { DEFAULT_DFX_HTTP_OPTIONS } from '../../../../constants';
import { useVerifyOTP } from '../../../../hooks/api-hooks/use-verify-otp';
import { useAutoReadOtp } from '../../../../hooks/use-auto-read-otp';
import Footer from '../../../layout/footer.component';
import { CountDownTimer } from './countdown-timer';
import { OTPField } from './otp-field';

const DEFAULT_OTP_TIMEOUT_SEC = 180; // 3 minutes
const OTP_LENGTH = 6;

interface Props {
  locationId: string;
  qwe: string;
  secretCode?: string;
  officeName: string;
  onFillManually: () => void;
  onSuccess: (
    data: Pick<
      FormsV1.Types.GetFormTemplateIO['output'],
      'personDetails' | 'medicalHistory' | 'prePopulateElements' | 'primaryInsurance' | 'secondaryInsurance'
    >
  ) => void;
}

export const OTPScreen: FC<Props> = ({ qwe, locationId, onFillManually, onSuccess }) => {
  const { t } = useTranslation('digital-forms-experience');
  const alerts = useAlert();
  const [isInvalidOtp, setIsInvalidOtp] = useState(false);
  const [otp, setOtp] = useState<string>('');
  const [userDetails, setUserDetails] = useState<FormsV1.Types.SendOTPIO['output'] | null>(null);
  const [isSentOtp, setIsSentOtp] = useState(false);

  const {
    count: countdownTimeInSeconds,
    restart: restartCountdown,
    isCompleted: isCountdownComplete,
  } = useCountdown({ initialValue: DEFAULT_OTP_TIMEOUT_SEC });
  const { start: restartAutoReadOtp } = useAutoReadOtp({
    onCode: setOtp,
    timeoutSec: DEFAULT_OTP_TIMEOUT_SEC,
    autoStart: true,
  });
  const sendOTPMutation = FormsV1.Mutations.useSendOTPMutation({ httpOptions: DEFAULT_DFX_HTTP_OPTIONS });
  const { verifyOTP, isVerifying } = useVerifyOTP(qwe, locationId);

  const requestOTP = useCallback(async (isResend: boolean) => {
    try {
      const res = await sendOTPMutation.mutateAsync({ id: qwe });
      setUserDetails(res);
      setIsSentOtp(true);
      restartCountdown();

      if (isResend) {
        alerts.success(t('Verification code sent successfully'));
      }
    } catch (error) {
      let isServerError = false;
      if (http.isHttpError(error)) {
        isServerError = (error.response?.status ?? 400) >= 500;
      }

      if (isResend || isServerError) {
        alerts.error(t('Failed to send verification code. Please fill out form manually if problem persists.'));
      } else {
        // we don't want to show error when we initiate a OTP request. In that case we directly redirect them to manual form
        onFillManually();
      }
    }
  }, []);

  useEffect(() => {
    requestOTP(false);
  }, [requestOTP]);

  const handleOTPChange = useCallback((value: string) => {
    if (value.length === OTP_LENGTH) setOtp(value);
    // Reset invalid OTP state on change
    setIsInvalidOtp(false);
  }, []);

  const handleVerifyOtp = async () => {
    const res = await verifyOTP(otp);
    if (res.isServerError) {
      alerts.error(t('Failed to verify code. Please fill out form manually if problem persists.'));
    } else if (!res.success || !res.data) {
      setIsInvalidOtp(true);
    } else {
      alerts.success(t('Code verified successfully'));
      onSuccess(res.data);
    }
  };

  const handleResendOtp = () => {
    restartAutoReadOtp();
    requestOTP(true);
  };

  return (
    <>
      <div css={containerStyle}>
        <ContentLoader show={!isSentOtp} message={t('Sending OTP')} />
        <ContentLoader show={isVerifying} message={t('Verifying . . .')} />
        <Heading level={3}>{t('Verify & Get Started')}</Heading>
        <Text textAlign='center' css={{ margin: theme.spacing(1, 0) }}>
          {t('To auto-fill your form, we need to validate your identity through a verification code.')}
        </Text>
        <Text textAlign='center' css={{ margin: theme.spacing(2, 0) }}>
          {t("We've sent a 6 digit code {{emailOrPhone}}", {
            emailOrPhone:
              userDetails?.email && userDetails?.phoneNumber
                ? `${t('to')} ${userDetails.email} ${t('and')} ${userDetails.phoneNumber}`
                : userDetails?.email
                ? `${t('to')} ${userDetails.email}`
                : `${t('at')} ${userDetails?.phoneNumber}`,
          })}
        </Text>
        <form>
          <FormRow>
            <OTPField
              value={otp}
              otpLength={OTP_LENGTH}
              disabled={isCountdownComplete}
              invalid={isInvalidOtp}
              onChange={handleOTPChange}
            />
          </FormRow>
          {isInvalidOtp && (
            <Text size='small' color='error'>
              {t('Please enter a valid code')}
            </Text>
          )}
        </form>
        <CountDownTimer timeInSeconds={countdownTimeInSeconds} />
        {isCountdownComplete ? (
          <Button size='small' onClick={handleResendOtp}>
            {t('Resend Code')}
          </Button>
        ) : (
          <>
            <Button disabled={otp.length !== OTP_LENGTH} size='small' onClick={handleVerifyOtp}>
              {t('Verify & Proceed')}
            </Button>
            <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
              <Text>{t("Didn't receive the code?")}</Text>
              <TextLink disabled>{t('Resend')}</TextLink>
            </div>
          </>
        )}
        <Text>{t('OR')}</Text>
        <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Text>{t("Don't have access to your phone or email?")}</Text>
          <TextLink textAlign='center' onClick={onFillManually}>
            {t('Fill out manually')}
          </TextLink>
        </div>
      </div>
      <Footer />
    </>
  );
};

const containerStyle = css({
  maxWidth: 600,
  width: '100%',
  margin: '0 auto',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.medium,
  boxShadow: theme.shadows.floating,
});
